import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { signal, computed } from '@angular/core';
import { TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EjournalService } from '../../services/ejournal.service';
import { finalize } from 'rxjs';
import { EtJournalSigner } from '@et/typings';
import { PresentationalAtomsModule } from '@et/presentational/atoms';

@Component({
  selector: 'et-journal-finalize-btn',
  imports: [PresentationalAtomsModule],
  templateUrl: './finalize-btn.component.html',
  styleUrl: './finalize-btn.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinalizeBtnComponent {
  readonly isFinalizing = signal<boolean>(false);
  readonly validationErrors = signal<string[]>([]);

  @ViewChild('finalizeEJournalModal')
  finalizeEJournalModal!: TemplateRef<unknown>;

  @ViewChild('validationErrorsModal')
  validationErrorsModal!: TemplateRef<unknown>;

  private dialog = inject(MatDialog);
  private ejournalService = inject(EjournalService);

  eJournal = this.ejournalService.eJournal;

  isPacketSigned = computed(
    () => this.ejournalService.selectedPacket()?.utcDateSigned !== null,
  );

  notarizedAuditLoaded = this.ejournalService.notarizedAuditLoaded;

  /**
   * Opens the finalize eJournal modal.
   */
  onFinalizeEJournal() {
    const errors = this.validateEJournal();
    if (errors && errors.length > 0) {
      this.validationErrors.set(errors);
      this.dialog.open(this.validationErrorsModal, {
        width: '100%',
        maxWidth: '700px',
        panelClass: ['et-dialog-radius', 'et-dialog-padding'],
        autoFocus: false,
      });
      return;
    } else {
      this.validationErrors.set([]);
    }

    this.dialog.open(this.finalizeEJournalModal, {
      width: '100%',
      maxWidth: '600px',
      panelClass: ['et-dialog-radius', 'et-dialog-padding'],
      autoFocus: false,
      id: 'finalize-ejournal-modal',
    });
  }

  /**
   * Finalizes the eJournal.
   */
  finalizeEJournal() {
    this.isFinalizing.set(true);
    this.ejournalService
      .finalizeEJournal()
      .pipe(finalize(() => this.isFinalizing.set(false)))
      .subscribe(() =>
        this.dialog.getDialogById('finalize-ejournal-modal')?.close(),
      );
  }

  /**
   * Validates the eJournal.
   * @returns An array of validation errors or null if there are no errors.
   */
  private validateEJournal(): string[] | null {
    const errors: string[] = [];

    const signers = this.ejournalService.signers();
    const seals = this.ejournalService.getSeals();

    // Check if assigned entries exist (placeholders are deleted on finalize)
    if (seals.length === 0) {
      errors.push('No eJournal seals found. Please add at least one seal.');
    }

    seals.forEach((seal) => {
      // Check deleted eSeals
      if (seal.source === 'deletedESeal') {
        errors.push(
          `Deleted eSeal ${seal.documentDescription} found. Please remove it.`,
        );
      }

      // Check seals
      if (seal.source === 'seal') {
        // Check if notarization date is present
        if (!seal.notarizationDate) {
          errors.push(
            `Seal on page ${seal.pageNumber} has no notarization date. Please add a notarization date.`,
          );
        }
      }

      // Checks for all valid seals
      if (seal.source === 'eSeal' || seal.source === 'seal') {
        // Check if no entries are created for eSeal or seal
        if (seal.entries.length === 0) {
          errors.push(
            `${
              seal.source.toString().charAt(0).toUpperCase() +
              seal.source.toString().slice(1)
            } on page ${
              seal.pageNumber
            } has no signers. Please add at least one signer.`,
          );
        }

        // Check if document name is present
        if (!seal.documentDescription) {
          errors.push(
            `Seal on page ${seal.pageNumber} has no document name. Please add a document name.`,
          );
        }
        // Check notorial act
        if (!seal.notorialAct) {
          errors.push(
            `Seal on page ${seal.pageNumber} has no notorial act. Please add a notorial act.`,
          );
        }
      }
    });

    // Check if signers exist
    if (signers.length === 0) {
      errors.push('No eJournal signers found. Please add at least one signer.');
    }

    errors.push(...this.validateSigners(signers));

    return errors.length ? errors : null;
  }

  /**
   * Validates that each signer has required fields, a signature, and at least one entry.
   */
  private validateSigners(signers: EtJournalSigner[]): string[] {
    const errors: string[] = [];

    signers.forEach((signer) => {
      const label = signer.name || '(unnamed)';

      if (!signer.name?.trim()) {
        errors.push(`Signer ${label} has no name. Please add a name.`);
      }

      if (!signer.state?.trim()) {
        errors.push(`Signer ${label} has no state. Please add a state.`);
      }

      if (!signer.address?.trim()) {
        errors.push(`Signer ${label} has no address. Please add an address.`);
      }

      const method = signer.identificationMethod;
      if (
        method !== 'identification_cards' &&
        method !== 'personal_knowledge' &&
        method !== 'credible_witnesses'
      ) {
        errors.push(
          `Signer ${label} has no identification method. Please add an identification method.`,
        );
      } else if (method === 'identification_cards') {
        if (!signer.idType?.trim()) {
          errors.push(`Signer ${label} has no ID type. Please add an ID type.`);
        }
        if (!signer.idNumber?.trim()) {
          errors.push(
            `Signer ${label} has no ID number. Please add an ID number.`,
          );
        }
        if (!signer.utcIdIssuedOn?.trim()) {
          errors.push(
            `Signer ${label} has no ID issue date. Please add an ID issue date.`,
          );
        }
        if (!signer.utcIdExpiresOn?.trim()) {
          errors.push(
            `Signer ${label} has no ID expiration date. Please add an ID expiration date.`,
          );
        }
      } else if (method === 'personal_knowledge') {
        if (!signer.personalKnowledgeStatement?.trim()) {
          errors.push(
            `Signer ${label} has no personal knowledge statement. Please add a statement.`,
          );
        }
      } else if (method === 'credible_witnesses') {
        if (!signer.witnessName?.trim()) {
          errors.push(
            `Signer ${label} has no witness name. Please add a witness name.`,
          );
        }
        if (!signer.witnessIdType?.trim()) {
          errors.push(
            `Signer ${label} has no witness ID type. Please add a witness ID type.`,
          );
        }
        if (!signer.witnessIdInfo?.trim()) {
          errors.push(
            `Signer ${label} has no witness ID info. Please add witness ID info.`,
          );
        }
      }

      // Check if all signer have signatures
      if (signer.signatures.length === 0) {
        errors.push(
          `Signer ${label} has no signature. Please add a signature.`,
        );
      }

      // Check if signer has at least one entry
      if (signer.ejournalEntryIds.length < 3) {
        errors.push(
          `Signer ${label} has no entries. Please add at least one entry.`,
        );
      }
    });

    return errors;
  }
}
